import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ContactForm = ({ currentLanguage }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    legalMatter: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const legalMatterOptions = [
    {
      value: 'corporate-law',
      label: currentLanguage === 'en' ? 'Corporate Law' : 'Sheria ya Makampuni'
    },
    {
      value: 'family-law',
      label: currentLanguage === 'en' ? 'Family Law' : 'Sheria ya Familia'
    },
    {
      value: 'property-law',
      label: currentLanguage === 'en' ? 'Property Law' : 'Sheria ya Mali'
    },
    {
      value: 'criminal-defense',
      label: currentLanguage === 'en' ? 'Criminal Defense' : 'Utetezi wa Jinai'
    },
    {
      value: 'employment-law',
      label: currentLanguage === 'en' ? 'Employment Law' : 'Sheria ya Ajira'
    },
    {
      value: 'other',
      label: currentLanguage === 'en' ? 'Other Legal Matter' : 'Jambo Lingine la Kisheria'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        legalMatter: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.legalMatter && formData.message;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-border p-6 lg:p-8">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          {currentLanguage === 'en' ? 'Send Us a Message' : 'Tutumie Ujumbe'}
        </h3>
        <p className="text-text-secondary">
          {currentLanguage === 'en' ?'Fill out the form below and we\'ll get back to you within 24 hours.' :'Jaza fomu hapa chini na tutakujibu ndani ya masaa 24.'
          }
        </p>
      </div>

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <Icon name="CheckCircle" size={20} color="#10B981" />
          <p className="text-green-800 text-sm">
            {currentLanguage === 'en' ?'Thank you! Your message has been sent successfully. We\'ll contact you soon.' :'Asante! Ujumbe wako umetumwa kwa ufanisi. Tutawasiliana nawe hivi karibuni.'
            }
          </p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <Icon name="AlertCircle" size={20} color="#EF4444" />
          <p className="text-red-800 text-sm">
            {currentLanguage === 'en' ?'Sorry, there was an error sending your message. Please try again.' :'Samahani, kulikuwa na hitilafu katika kutuma ujumbe wako. Tafadhali jaribu tena.'
            }
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-2">
              {currentLanguage === 'en' ? 'Full Name' : 'Jina Kamili'} *
            </label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={currentLanguage === 'en' ? 'Enter your full name' : 'Ingiza jina lako kamili'}
              required
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              {currentLanguage === 'en' ? 'Email Address' : 'Anwani ya Barua Pepe'} *
            </label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={currentLanguage === 'en' ? 'Enter your email address' : 'Ingiza anwani yako ya barua pepe'}
              required
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
              {currentLanguage === 'en' ? 'Phone Number' : 'Nambari ya Simu'} *
            </label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+254 XXX XXX XXX"
              required
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="legalMatter" className="block text-sm font-medium text-text-primary mb-2">
              {currentLanguage === 'en' ? 'Legal Matter Type' : 'Aina ya Jambo la Kisheria'} *
            </label>
            <select
              id="legalMatter"
              name="legalMatter"
              value={formData.legalMatter}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-text-primary"
            >
              <option value="">
                {currentLanguage === 'en' ? 'Select legal matter type' : 'Chagua aina ya jambo la kisheria'}
              </option>
              {legalMatterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
            {currentLanguage === 'en' ? 'Message' : 'Ujumbe'} *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder={currentLanguage === 'en' ?'Please describe your legal matter in detail...' :'Tafadhali eleza jambo lako la kisheria kwa undani...'
            }
            required
            rows={5}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            type="submit"
            variant="primary"
            disabled={!isFormValid || isSubmitting}
            loading={isSubmitting}
            iconName="Send"
            iconPosition="right"
            className="flex-1"
          >
            {isSubmitting 
              ? (currentLanguage === 'en' ? 'Sending...' : 'Inatuma...')
              : (currentLanguage === 'en' ? 'Send Message' : 'Tuma Ujumbe')
            }
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setFormData({
              name: '',
              email: '',
              phone: '',
              legalMatter: '',
              message: ''
            })}
            iconName="RotateCcw"
            iconPosition="left"
          >
            {currentLanguage === 'en' ? 'Clear Form' : 'Futa Fomu'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;